/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ActiveCell, ActiveCellDetail, ScrollDetail, ViewportInfo } from '../List';

import { findElementByKey } from '#utils/PRIVATE_collectionUtils';
import { getRowKey } from '#PRIVATE_TableCommon/utils/DomUtils';
import { isLogicalCtrl } from '#PRIVATE_TableCommon/utils/EventUtils';
import {
  getElementFromCell,
  getLastRenderedRowKey,
  getRowIndexForRowKey,
  getRowKeyForRowIndex,
  hasLoadMoreCell,
  isAutoFocusCell,
  LIST_ROW_SELECTOR
} from './ListDomUtils';
import { cellComparator } from './ListFocusUtils';
import { getScrollerBottom, getScrollPosition } from './ListScrollUtils';

/**
 * Helper function to handle an 'Up' or 'Down' arrow key press.
 * Up - Moves to the same cell in the previous row (including headers and footers). No op if already on the first row.
 * Down - Moves to the same cell in the next row (including headers and footers). No op if already on the last row.
 */
export function handleAdjacentRowGesture<K extends string | number>(
  rootElement: HTMLDivElement,
  listId: string,
  initialCell: ActiveCell<K>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  isPrevious: boolean,
  isExtendSelectionGesture: boolean,
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K>) => void
) {
  let newCell = initialCell;
  const cellType = initialCell.type;
  if (cellType === 'loadMore') {
    if (isPrevious) {
      const rowKey = getLastRenderedRowKey<K>(rootElement, listId);
      if (rowKey != null) {
        newCell = { rowKey: rowKey, type: 'data' };
        onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, listId, newCell));
      }
    }
  } else {
    if (cellType === 'data') {
      const currentRowKey = initialCell.rowKey;
      const currentRowElement = findElementByKey(
        rootElement,
        currentRowKey,
        `[${LIST_ROW_SELECTOR}='${listId}']`
      );
      if (currentRowElement != null) {
        const rowElements = [...rootElement.querySelectorAll(`[${LIST_ROW_SELECTOR}='${listId}']`)];
        const currentRowIndex = rowElements.indexOf(currentRowElement);
        let adjacentRowElement;
        if (isPrevious && currentRowIndex > 0) {
          adjacentRowElement = rowElements[currentRowIndex - 1] as HTMLElement;
        } else if (!isPrevious && currentRowIndex < rowElements.length - 1) {
          adjacentRowElement = rowElements[currentRowIndex + 1] as HTMLElement;
        }
        if (adjacentRowElement != null) {
          const rowKey = getRowKey<K>(adjacentRowElement);
          if (rowKey != null) {
            newCell = { rowKey: rowKey, type: 'data' };
            onActiveCellChanged(
              { value: newCell },
              isAutoFocusCell(rootElement, listId, newCell),
              isExtendSelectionGesture
            );
          }
        } else if (
          !isExtendSelectionGesture &&
          !isPrevious &&
          hasLoadMoreCell(rootElement, listId)
        ) {
          newCell = { type: 'loadMore' };
          if (getElementFromCell(rootElement, listId, newCell) != null) {
            onActiveCellChanged({ value: newCell }, isAutoFocusCell(rootElement, listId, newCell));
          }
        }
      }
    }
  }
  if (cellComparator(initialCell, newCell)) {
    updateDeferredScrollDetail({ cell: newCell, locationX: 'inView', locationY: 'inView' });
  }
}

/**
 * Helper function to handle a 'PageUp' or 'PageDown' arrow key press.
 * PageUp - Moves to the same cell in the first visible row in the current viewport,
 * and scrolls the List so it becomes the last visible row in the viewport when able.
 * PageDown - Moves to the same cell in the last visible row in the current viewport,
 * and scrolls the List so it becomes the first visible row in the viewport when able.
 */
export function handlePageRowGesture<K extends string | number>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  listId: string,
  initialCell: ActiveCell<K>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  isPrevious: boolean,
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K>) => void
) {
  if (initialCell.type !== 'noData') {
    const rowElements = rootElement.querySelectorAll(`[${LIST_ROW_SELECTOR}='${listId}']`);
    if (rowElements.length > 0) {
      const scrollPosition = getScrollPosition(rootElement, scrollerElement, viewportInfo, listId);
      const verticalRowIndex = getRowIndexForRowKey(rootElement, listId, scrollPosition.key!);
      let targetRowIndex;
      if (isPrevious) {
        if (scrollPosition.offsetY === 0) {
          targetRowIndex = Math.max(verticalRowIndex! - 1, 0);
        } else {
          targetRowIndex = verticalRowIndex!;
        }
      } else {
        const scrollerBottom = getScrollerBottom(scrollerElement, viewportInfo.viewportConfig);
        targetRowIndex = verticalRowIndex!;
        while (rowElements.length > targetRowIndex + 1) {
          targetRowIndex += 1;
          const elementBottom = rowElements[targetRowIndex].getBoundingClientRect().bottom;
          if (elementBottom > scrollerBottom) {
            break;
          }
        }
      }
      const newRowKey = getRowKeyForRowIndex<K>(rootElement, listId, targetRowIndex);
      if (newRowKey != null) {
        const newCell: ActiveCell<K> = {
          rowKey: newRowKey,
          type: 'data'
        };
        if (newRowKey !== initialCell.rowKey) {
          onActiveCellChanged(
            { value: newCell },
            isAutoFocusCell(rootElement, listId, newCell),
            false,
            true
          );
        }
        updateDeferredScrollDetail({
          cell: newCell,
          locationX: 'inView',
          locationY: isPrevious ? 'bottom' : 'top'
        });
      }
    }
  }
}

export function isReorderGesture(event: KeyboardEvent) {
  return event.shiftKey && isLogicalCtrl(event);
}
